// ═══════════════════════════════════════════════════════════════
// LmsAssessmentBuilder — création / édition d'une évaluation
// QCM auto-noté (mono-choix) OU réponses ouvertes notées V/R/E/T (spectral).
// Schéma de question aligné sur LmsCoursePlayer :
//   QCM      → { id, type:'qcm', text, options:[…], correct:<index> }
//   Spectral → { id, type:'open', text, expected }
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { ClipboardCheck, Save, Plus, Trash2, CheckCircle2, Circle } from 'lucide-react';
import { lmsApi } from '../../services/lmsApi';
import { toast } from '../ui/Toast';
import { Modal, Field, TextInput, TextArea, s, Spinner, ACCENT } from './lmsUi';

const newId = () => 'q' + Math.random().toString(36).slice(2, 9);

function blankQcm() { return { id: newId(), type: 'qcm', text: '', options: ['', ''], correct: 0 }; }
function blankOpen() { return { id: newId(), type: 'open', text: '', expected: '' }; }

export default function LmsAssessmentBuilder({ courseId, assessment, onClose, onSaved }) {
    const editing = !!assessment;
    const initSpectral = !!assessment?.use_spectral;
    const [spectral, setSpectral] = useState(initSpectral);
    const [title, setTitle] = useState(assessment?.title || '');
    const [passThreshold, setPassThreshold] = useState(assessment?.pass_threshold != null ? String(assessment.pass_threshold) : '70');
    const [maxAttempts, setMaxAttempts] = useState(assessment?.max_attempts != null ? String(assessment.max_attempts) : '3');
    const [rubric, setRubric] = useState(
        assessment?.spectral_rubric && typeof assessment.spectral_rubric === 'object'
            ? (assessment.spectral_rubric.consignes || '') : ''
    );
    const [questions, setQuestions] = useState(() => {
        const qs = Array.isArray(assessment?.questions) ? assessment.questions : [];
        if (!qs.length) return [initSpectral ? blankOpen() : blankQcm()];
        return qs.map((q) => initSpectral
            ? { id: q.id || newId(), type: 'open', text: q.text || '', expected: q.expected || '' }
            : { id: q.id || newId(), type: 'qcm', text: q.text || '', options: q.options?.length ? [...q.options] : ['', ''], correct: typeof q.correct === 'number' ? q.correct : 0 });
    });
    const [saving, setSaving] = useState(false);

    // Bascule de mode : on réinitialise les questions au format cible.
    const switchMode = (toSpectral) => {
        if (toSpectral === spectral) return;
        setSpectral(toSpectral);
        setQuestions([toSpectral ? blankOpen() : blankQcm()]);
    };

    const updateQ = (id, patch) => setQuestions((qs) => qs.map((q) => q.id === id ? { ...q, ...patch } : q));
    const removeQ = (id) => setQuestions((qs) => qs.length > 1 ? qs.filter((q) => q.id !== id) : qs);
    const addQ = () => setQuestions((qs) => [...qs, spectral ? blankOpen() : blankQcm()]);

    const setOption = (qid, oi, val) => setQuestions((qs) => qs.map((q) => q.id === qid ? { ...q, options: q.options.map((o, i) => i === oi ? val : o) } : q));
    const addOption = (qid) => setQuestions((qs) => qs.map((q) => q.id === qid ? { ...q, options: [...q.options, ''] } : q));
    const removeOption = (qid, oi) => setQuestions((qs) => qs.map((q) => {
        if (q.id !== qid || q.options.length <= 2) return q;
        const options = q.options.filter((_, i) => i !== oi);
        let correct = q.correct;
        if (oi === q.correct) correct = 0;
        else if (oi < q.correct) correct = q.correct - 1;
        return { ...q, options, correct };
    }));

    // Validation
    const validate = () => {
        if (!title.trim()) return 'Donne un titre à l\'évaluation.';
        for (const [i, q] of questions.entries()) {
            if (!q.text.trim()) return `Question ${i + 1} : le texte est vide.`;
            if (!spectral) {
                const filled = q.options.filter((o) => o.trim());
                if (filled.length < 2) return `Question ${i + 1} : au moins 2 réponses non vides.`;
                if (!q.options[q.correct]?.trim()) return `Question ${i + 1} : la bonne réponse cochée est vide.`;
            }
        }
        return null;
    };

    const save = async () => {
        const error = validate();
        if (error) { toast.error(error); return; }
        setSaving(true);
        try {
            const payload = {
                title: title.trim(),
                type: spectral ? 'spectral' : 'quiz',
                use_spectral: spectral,
                pass_threshold: passThreshold === '' ? 70 : parseFloat(passThreshold),
                max_attempts: maxAttempts === '' ? null : parseInt(maxAttempts, 10),
                questions: spectral
                    ? questions.map((q) => ({ id: q.id, type: 'open', text: q.text.trim(), expected: q.expected.trim() || undefined }))
                    : questions.map((q) => ({ id: q.id, type: 'qcm', text: q.text.trim(), options: q.options.map((o) => o.trim()).filter(Boolean), correct: q.correct })),
                spectral_rubric: spectral && rubric.trim() ? { consignes: rubric.trim() } : null,
            };
            if (editing) {
                await lmsApi.updateAssessment(assessment.id, payload);
                toast.success('Évaluation mise à jour');
            } else {
                await lmsApi.createAssessment(courseId, payload);
                toast.success('Évaluation créée');
            }
            onSaved?.();
        } catch (e) {
            toast.error(e.message || 'Échec de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            title={editing ? 'Modifier l\'évaluation' : 'Nouvelle évaluation'}
            subtitle={spectral ? 'Réponses ouvertes notées par l\'IA (Véracité · Rigueur · Exhaustivité · Traçabilité)' : 'QCM auto-noté (une bonne réponse par question)'}
            icon={ClipboardCheck}
            onClose={onClose}
            busy={saving}
            width={680}
            footer={
                <>
                    <button style={s.ghostBtn} onClick={onClose} disabled={saving}>Annuler</button>
                    <button style={{ ...s.primaryBtn, opacity: saving ? 0.6 : 1 }} onClick={save} disabled={saving}>
                        {saving ? <Spinner /> : <Save size={14} />} {editing ? 'Enregistrer' : 'Créer'}
                    </button>
                </>
            }
        >
            <Field label="Titre">
                <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Quiz de validation — Module 1" autoFocus />
            </Field>

            <Field label="Mode de notation">
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={() => switchMode(false)} style={s.chip(!spectral)}>QCM auto-noté</button>
                    <button type="button" onClick={() => switchMode(true)} style={s.chip(spectral)}>Spectral (réponses ouvertes)</button>
                </div>
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <Field label="Seuil de réussite (%)">
                    <TextInput type="number" min="0" max="100" step="1" value={passThreshold} onChange={(e) => setPassThreshold(e.target.value)} />
                </Field>
                <Field label="Tentatives max" hint="Vide = illimité.">
                    <TextInput type="number" min="1" step="1" value={maxAttempts} onChange={(e) => setMaxAttempts(e.target.value)} placeholder="illimité" />
                </Field>
            </div>

            <div style={{ height: 1, background: 'var(--card-border)', margin: '4px 0 18px' }} />

            {questions.map((q, i) => (
                <div key={q.id} style={{ ...s.card, marginBottom: 14, position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>Question {i + 1}</span>
                        {questions.length > 1 && (
                            <button type="button" onClick={() => removeQ(q.id)} style={{ ...s.iconBtn, width: 26, height: 26, color: '#ef4444' }} title="Supprimer">
                                <Trash2 size={13} />
                            </button>
                        )}
                    </div>
                    <TextInput value={q.text} onChange={(e) => updateQ(q.id, { text: e.target.value })} placeholder="Énoncé de la question" style={{ marginBottom: 10 }} />

                    {!spectral ? (
                        <div>
                            <div style={{ ...s.hint, marginTop: 0, marginBottom: 6 }}>Coche la bonne réponse.</div>
                            {q.options.map((opt, oi) => (
                                <div key={oi} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                    <button type="button" onClick={() => updateQ(q.id, { correct: oi })} style={{ ...s.iconBtn, width: 28, height: 28, border: 'none', background: 'transparent', color: q.correct === oi ? '#22c55e' : 'var(--text-muted)' }} title="Bonne réponse">
                                        {q.correct === oi ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                                    </button>
                                    <TextInput value={opt} onChange={(e) => setOption(q.id, oi, e.target.value)} placeholder={`Réponse ${oi + 1}`} style={{ flex: 1 }} />
                                    {q.options.length > 2 && (
                                        <button type="button" onClick={() => removeOption(q.id, oi)} style={{ ...s.iconBtn, width: 28, height: 28, color: 'var(--text-muted)' }} title="Retirer"><Trash2 size={12} /></button>
                                    )}
                                </div>
                            ))}
                            <button type="button" onClick={() => addOption(q.id)} style={{ ...s.ghostBtn, padding: '6px 10px', fontSize: 12 }}><Plus size={12} /> Ajouter une réponse</button>
                        </div>
                    ) : (
                        <Field label="Repère de correction (optionnel)" hint="Ce qui est attendu — guide l'IA dans la notation, jamais montré à l'apprenant." style={{ marginBottom: 0 }}>
                            <TextArea value={q.expected} onChange={(e) => updateQ(q.id, { expected: e.target.value })} placeholder="Points clés attendus dans une bonne réponse…" style={{ minHeight: 70 }} />
                        </Field>
                    )}
                </div>
            ))}

            <button type="button" onClick={addQ} style={{ ...s.ghostBtn, width: '100%', justifyContent: 'center' }}><Plus size={14} /> Ajouter une question</button>

            {spectral && (
                <Field label="Grille de notation globale (optionnel)" style={{ marginTop: 18 }} hint="Consignes transversales pour l'IA évaluatrice (ton, exigences, barème spécifique).">
                    <TextArea value={rubric} onChange={(e) => setRubric(e.target.value)} placeholder="Ex. Exiger au moins un exemple concret ; pénaliser les généralités ; valoriser la nuance." />
                </Field>
            )}
        </Modal>
    );
}
